import currency from 'currency.js';
import moment from 'moment';

import i18n from '_/i18n';

const MAX_SIGNED_INT = Math.pow(2, 31) - 1;

export const requiredRule = {
  required: {
    value: true,
    message: i18n.t('common:errors.inputs.emptyField'),
  },
};

export const requiredAmountRule = {
  ...requiredRule,
  validate: (value: string): true | string =>
    !isNaN(Number(value)) ||
    i18n.t('common:errors.inputs.invalidAmount').toString(),
};

export const emptyOrValidAmountRule = {
  validate: (value: string): true | string =>
    value === '' ||
    !isNaN(Number(value)) ||
    i18n.t('common:errors.inputs.invalidAmount').toString(),
};

export function getRequiredRule(required: boolean = false): {
  required: {value: boolean; message: string};
} {
  return {
    required: {
      value: required,
      message: i18n.t('common:errors.inputs.emptyField'),
    },
  };
}

export const validateNonWhitespace = (value: string): true | string =>
  value.trim() !== '' ||
  i18n.t('common:errors.inputs.emptyFieldWithWhitespaces').toString();

export const requiredAndNonWhitespaceRule = {
  ...requiredRule,
  validate: validateNonWhitespace,
};

export const nonRequiredAndNonWhitespaceRule = {
  validate: (value: string): true | string =>
    value.trim() !== '' ||
    value === '' ||
    i18n.t('common:errors.inputs.noWhitespaces').toString(),
};

// the `required` is necessary for it to be applied as a rule
export const notRequiredRule = {
  required: {
    value: false,
    message: '',
  },
  validate: undefined,
};

export const positiveAmountRule = {
  validate: (value: string): true | string =>
    currency(value).intValue > 0 ||
    i18n.t('common:errors.inputs.amountTooLow', {limit: '0'}).toString(),
};

export const requiredPositiveAmountRule = {
  ...requiredRule,
  ...positiveAmountRule,
};

export const requiredNonNegativeAmountRule = {
  ...requiredRule,
  validate: (value: string): true | string =>
    currency(value).intValue >= 0 ||
    i18n.t('common:errors.inputs.amountNegative').toString(),
};

export const requiredNonZeroAmountRule = {
  ...requiredRule,
  validate: (value: string): true | string =>
    currency(value).intValue !== 0 ||
    i18n.t('common:errors.inputs.amountZero').toString(),
};

export const getLimitedTextLengthRule = (
  textLengthLimit: number,
  required?: boolean
): {
  validate: (value: string) => true | string;
} => ({
  ...(required ? requiredRule : {}),
  validate: (value: string): true | string =>
    value.length <= textLengthLimit ||
    i18n
      .t('common:errors.inputs.textLimitOverstepped', {
        limit: textLengthLimit,
      })
      .toString(),
});

const validatePositiveInt = (
  value: string,
  requireNonZero: boolean = false
): true | string => {
  const isInt = /^\d+$/u.test(value);
  const intValue = parseInt(value, 10);
  if (isNaN(intValue) || !isInt) {
    return i18n.t('common:errors.inputs.invalidNumber').toString();
  } else if (intValue > MAX_SIGNED_INT) {
    return i18n.t('common:errors.inputs.intTooLarge').toString();
  } else if (requireNonZero && intValue === 0) {
    return i18n.t('common:errors.inputs.amountZero').toString();
  }
  return true;
};

export const nonNegativeIntRule = {
  ...requiredRule,
  validate: (value: string): true | string => validatePositiveInt(value, false),
};

export const positiveIntRule = {
  ...requiredRule,
  validate: (value: string): true | string => validatePositiveInt(value, true),
};

export const positiveTwoDecimalsRule = {
  validate: (value: string): true | string => {
    const hasMaxTwoDecimals = /^\d+(\.\d{1,2})?$/u.test(value);
    return (
      hasMaxTwoDecimals ||
      i18n.t('common:errors.inputs.invalidNumber').toString()
    );
  },
};

export const validDateRule = {
  validate: {
    date: (value: string | null | undefined): true | string =>
      !value ||
      /^\d{4}-\d{2}-\d{2}$/u.test(value) ||
      i18n.t('common:errors.inputs.invalidDateField').toString(),
  },
};

export const validateEmailAddressRule = {
  validate: (value: string): true | string => {
    const isEmailAddress =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/u.test(
        value
      );
    return isEmailAddress || i18n.t('common:errors.inputs.invalidEmailAddress');
  },
};

export const parsableNonZeroDuration = {
  validate: (value: string): true | string =>
    /* Test formats like 30h59m or 23h, disallow 0h or 0h 0min */
    /^((([1-9][0-9]*)\s*h\s*(0?([0-9]|[1-5][0-9])\s*(m|min)?)?)|(0\s*h\s*(0?[1-9]|[1-5][0-9])\s*(m|min)?))$/u.test(
      value
    ) ||
    /* Test format 124min, without max*/
    /^[1-9][0-9]*\s*(m|min)$/u.test(value) ||
    /* check for hour fractions 1.25 */
    /^((([1-9]|1[0-9]|2[0-3])\.([0-9]{1,2})?)|(0.(0?[1-9]|[1-9][0-9])))\s*h?$/u.test(
      value
    ) ||
    /* check for integers */
    /^([1-9][0-9]*)(\.|h)?$/u.test(value) ||
    /* check for hour and minutes with : - 1:, :1 and 0:00 are not allowed */
    /^((([1-9]|1[0-9]|2[0-3]):(0[0-9]|[1-5][0-9]))|(0:((0[1-9])|([1-5][0-9]))))$/u.test(
      value
    ) ||
    i18n.t('common:errors.inputs.invalidFreeformDurationInput').toString(),
};

export const parsableDuration = {
  validate: (value: string): true | string =>
    /* Test formats  like 30h59m or 23h */
    /^((0|[1-9][0-9]*)\s*h\s*((00?|0?[1-9]|[1-5][0-9])\s*(m|min)?)?)$/u.test(
      value
    ) ||
    /* Test format 124min, without max*/
    /^(0|[1-9][0-9]*)\s*(m|min)$/u.test(value) ||
    /* check for hour fractions 1.25 */
    /^(([0-9]|1[0-9]|2[0-3])\.([0-9]{1,2})?)\s*h?$/u.test(value) ||
    /* check for integers */
    /^(0|[1-9][0-9]*)(\.|h)?$/u.test(value) ||
    /* check for hour and minutes with : - 1:, :1 and 0:00 are not allowed */
    /^(([0-9]|1[0-9]|2[0-3]):([0-5][0-9]))$/u.test(value) ||
    i18n.t('common:errors.inputs.invalidFreeformDurationInput').toString(),
};

export const validateNoPastTime = (
  valueDate: string,
  valueTime: string
): true | string => {
  const timeNow = moment();
  const timeInput = moment(`${valueDate}T${valueTime}:00Z`).utc();
  const offset = new Date().getTimezoneOffset();
  timeInput.add(offset, 'minutes');
  if (timeInput.isBefore(timeNow)) {
    return i18n.t('errors.inputs.timeFieldInThePast').toString();
  }
  return true;
};

export function validateMinDate(
  minDate: string
): (value: string) => true | string {
  return (value: string): true | string => {
    if (moment(value).isSameOrAfter(moment(minDate))) {
      return true;
    }
    return i18n.t('common:validations.date.minDate', {
      minDate: new Date(minDate),
    });
  };
}

export const validateDateBeforeDueDate = (
  date: string,
  dueDate: string
): true | string =>
  moment(dueDate).isBefore(moment(date))
    ? i18n.t('errors.inputs.dateIsPastDueDate').toString()
    : true;
